import { forwardRef, useMemo, useState } from "react";
import {
  ActionMeta,
  ControlProps,
  MenuProps,
  OptionTypeBase,
  Props as SelectProps,
  components,
} from "react-select";

import cn from "@/helpers/classNames";

import { Button, PrimaryButton } from "./Button";
import InputContainerButton from "./InputContainer";
import Select from "./Select";

const Control = ({ children, ...props }: ControlProps<any, false>) => {
  const {
    selectProps: { descriptionKey },
    isDisabled,
  } = props;
  const hasDescription = useMemo(
    () => !!props.options?.some((v) => v[descriptionKey]),
    [descriptionKey, props.options],
  );
  return (
    <components.Control {...props}>
      <InputContainerButton
        isDisabled={isDisabled}
        className="h-auto"
        isFocused={props.isFocused}
      >
        <div
          className={cn("flex max-h-24 w-full overflow-y-auto py-2.5", {
            "h-16": hasDescription,
            "h-auto": props.isMulti,
          })}
        >
          {children}
        </div>
      </InputContainerButton>
    </components.Control>
  );
};

const Menu = ({ children, ...props }: MenuProps<any, true>) => {
  const selectAll = () => {
    const { filtered, isFiltered, getOptionValue } = getFilteredOptions();
    const currentValue = (props.selectProps.value ?? []) as any[];

    let optionsToSelect = filtered;
    if (isFiltered) {
      // Preserve already-selected options that are outside the filtered view
      const selectingValues = new Set(filtered.map((o) => getOptionValue(o)));
      const preserved = currentValue.filter(
        (o) => !selectingValues.has(getOptionValue(o)),
      );
      optionsToSelect = [...preserved, ...filtered];
    }

    props.selectProps.onChange?.(optionsToSelect, {
      action: "select-option",
      option: optionsToSelect,
    });
    props.selectProps.onInputChange?.("", { action: "input-change" });
  };

  const getFilteredOptions = () => {
    const { inputValue, filterOption } = props.selectProps;
    const getOptionLabel =
      props.selectProps.getOptionLabel ?? ((o: any) => o.label);
    const getOptionValue =
      props.selectProps.getOptionValue ?? ((o: any) => o.value);
    if (!inputValue)
      return {
        filtered: props.options as any[],
        isFiltered: false,
        getOptionValue,
      };
    const filtered = (props.options as any[]).filter((option) => {
      const label = getOptionLabel(option);
      if (filterOption) {
        return filterOption(
          { label, value: getOptionValue(option), data: option },
          inputValue,
        );
      }
      return label.toLowerCase().includes(inputValue.toLowerCase());
    });
    return { filtered, isFiltered: true, getOptionValue };
  };

  const deselectAll = () => {
    const { filtered, isFiltered, getOptionValue } = getFilteredOptions();
    if (isFiltered) {
      // Only deselect the filtered options, preserve selections outside the filter
      const filteredValues = new Set(filtered.map((o) => getOptionValue(o)));
      const remaining = ((props.selectProps.value ?? []) as any[]).filter(
        (o) => !filteredValues.has(getOptionValue(o)),
      );
      props.selectProps.onChange?.(remaining, {
        action: "deselect-option",
        option: filtered,
      });
    } else {
      props.selectProps.onChange?.([], {
        action: "deselect-option",
        option: props.options,
      });
    }
    props.selectProps.onInputChange?.("", { action: "input-change" });
  };

  const applySelection = () => {
    props.selectProps.onMenuClose?.();
  };

  const { filtered, isFiltered, getOptionValue } = getFilteredOptions();
  const currentValue = (props.selectProps.value ?? []) as any[];
  const selectedValues = new Set(currentValue.map((o) => getOptionValue(o)));
  const totalCount = (props.options as any[]).length;
  const selectedCount = currentValue.length;
  const unselectedFilteredCount = filtered.filter(
    (o) => !selectedValues.has(getOptionValue(o)),
  ).length;
  const selectedFilteredCount = filtered.filter((o) =>
    selectedValues.has(getOptionValue(o)),
  ).length;

  return (
    <components.Menu {...props}>
      {props.selectProps.isLoading ? (
        <div className="p-2 text-center text-base leading-6 text-gray-400 dark:bg-gray-700">
          Loading...
        </div>
      ) : (
        <div>
          {children}
          {totalCount > 1 && (
            <div className="flex w-full items-center justify-between border-t px-4 py-2 dark:border-t-gray-800 dark:bg-gray-700">
              <div className="text-sm">
                <Button
                  variant="link"
                  size="sm"
                  onClick={selectAll}
                  disabled={unselectedFilteredCount === 0}
                >
                  {isFiltered ? "Select filtered" : "Select all"}
                </Button>
                <span className="mx-2">|</span>
                <Button
                  variant="link"
                  size="sm"
                  disabled={selectedFilteredCount === 0}
                  onClick={deselectAll}
                >
                  {isFiltered ? "Deselect filtered" : "Deselect all"}
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-400">
                  {selectedCount} of {totalCount} selected
                </span>
                <PrimaryButton onClick={applySelection} size="sm">
                  Apply selection
                </PrimaryButton>
              </div>
            </div>
          )}
        </div>
      )}
    </components.Menu>
  );
};

export const AppliedMultiSelect = forwardRef(
  <OptionType extends OptionTypeBase = { label: string; value: string }>(
    props: SelectProps<OptionType, true>,
    ref: React.Ref<any>,
  ) => {
    const [isMenuOpen, setMenuOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<OptionType[]>(() => {
      if (props.value) {
        if (Array.isArray(props.value)) {
          // TypeScript does not infer readonly OptionType[] to be OptionType[] automatically.
          // The spread fixes this.
          return [...props.value];
        }
        return [props.value];
      }
      return [];
    });
    return (
      <Select
        ref={ref}
        {...props}
        value={selectedOptions}
        onChange={(
          options: OptionType[],
          actionMeta: ActionMeta<OptionType>,
        ) => {
          setSelectedOptions(options);
          if (
            // Remove button on item in the input clicked
            actionMeta.action === "remove-value" ||
            // Backspace pressed on input
            actionMeta.action === "pop-value" ||
            // Clear (all) button clicked
            actionMeta.action === "clear"
          ) {
            // Change made from the input - apply changes immediately
            props.onChange?.(options, actionMeta);
          }
        }}
        menuIsOpen={isMenuOpen}
        onMenuClose={() => {
          setMenuOpen(false);
          // Apply changes when the menu is closed
          props.onChange?.(selectedOptions, {
            action: "select-option",
            option: selectedOptions[selectedOptions.length - 1],
          });
        }}
        onMenuOpen={() => {
          setMenuOpen(true);
        }}
        components={{
          Control,
          Menu,
        }}
        closeMenuOnSelect={(props.options?.length ?? 0) <= 1}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
      />
    );
  },
);
